/** Reads the spread service's current per-grid-cell wind read model (GET /weather?cells=)
 *  and adapts it into synthetic app.aid.weather.conditions events so the existing WeatherStore
 *  folds the seed exactly like live records — letting the weather layer live-tail instead of
 *  replaying the firehose from seq 0. Read-only.
 *
 *  Like SpreadClient (and unlike IncidentsClient), the endpoint IS the base path, so
 *  weatherInCells appends only the query string (`${baseUrl}?cells=…`). */

/** One reading from GET /weather — the flat shape the Rust read API serves. */
export interface ReadModelWeather {
  cell: string;
  gridRes: number;
  windFromDeg: number;
  speedKmh: number;
  gustKmh?: number;
}

export interface WeatherClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

export class WeatherClient {
  constructor(private readonly opts: WeatherClientOptions) {}
  private get fetch(): typeof fetch {
    return this.opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Current per-grid-cell wind whose grid cell covers any of `cells` (O(grid cells), no history). */
  async weatherInCells(cells: string[]): Promise<ReadModelWeather[]> {
    if (cells.length === 0) return [];
    const res = await this.fetch(`${this.opts.baseUrl}?cells=${encodeURIComponent(cells.join(","))}`);
    if (!res.ok) throw new Error(`weather fetch failed: ${res.status} ${res.statusText}`);
    return (await res.json()) as ReadModelWeather[];
  }
}

/** Convert read-model wind to synthetic app.aid.weather.conditions events. seq 0 ⇒ any
 *  live-tailed record for the same grid cell wins (WeatherStore latest-wins by seq). Pure. */
export function seedWeatherEventsFromReadModel(
  views: ReadModelWeather[],
): Array<{ seq: number; json: Record<string, unknown> }> {
  return views.map((v) => ({
    seq: 0,
    json: {
      $type: "app.aid.weather.conditions",
      payload: {
        cell: v.cell,
        gridRes: v.gridRes,
        windFromDeg: v.windFromDeg,
        speedKmh: v.speedKmh,
        ...(v.gustKmh !== undefined ? { gustKmh: v.gustKmh } : {}),
      },
    },
  }));
}
